import 'dayjs/locale/es';

import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';

import {
  ControlGroupIcon,
  ControlIcon,
  EyeIcon,
  EyeOffIcon,
} from '../../assets';
import { Breadcrumbs } from '../../components/common/Breadcrumbs';
import { Button } from '../../components/common/Button';
import { GroupedListelement } from '../../components/control/GroupedListElement';
import { InfinitePieceList } from '../../components/control/InfinitePieceList';
import { ActiveFilters } from '../../components/filters/ActiveFilters';
import { ControlPiecesFilters } from '../../components/filters/ControlPiecesFilters';
import { ButtonDropdown } from '../../components/inputs/ButtonDropdown';
import MonthDateFromTo from '../../components/inputs/MonthDateFromTo';
import { TableSearchInput } from '../../components/inputs/TableSearchInput';
import Loader from '../../components/loaders/LoaderSpinner';
import {
  CONTROL_GROUP_DROPDOWN_OPTIONS,
  CONTROL_INFINITE_SCROLLING_PAGE_SIZE,
} from '../../config/constants';
import { replaceIds, ROUTES } from '../../config/routes/config';
import { LIST_PIECES } from '../../graphql/pieces/query';
import { controlScheduledPiecesFilterAtom } from '../../states/atoms';
import type {
  AccessDataListPieces,
  ControlPiecesFilters as ControlPiecesFiltersType,
  GroupedPiece,
  SinglePiece,
} from '../../types/control';

dayjs.extend(localizedFormat);

function ControlPieces() {
  const { t, i18n } = useTranslation('common');
  dayjs.locale(i18n.language);
  const navigate = useNavigate();

  const [pageIndex, setPageIndex] = useState<number>(0);
  const [data, setData] = useState<SinglePiece[]>([]);
  const [count, setCount] = useState<number>(0);
  const [groupedData, setGroupedData] = useState<GroupedPiece[]>([]);
  const [filters, setFilters] = useRecoilState(
    controlScheduledPiecesFilterAtom,
  );

  const {
    loading: loadingQuery,
    error,
    refetch,
  } = useQuery<AccessDataListPieces>(LIST_PIECES, {
    variables: {
      ...filters,
      influencers: filters?.influencers?.map((i) => i.value),
      client: filters?.client?.map((i) => i.value),
      offset: pageIndex * CONTROL_INFINITE_SCROLLING_PAGE_SIZE,
      limit: CONTROL_INFINITE_SCROLLING_PAGE_SIZE,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (res: AccessDataListPieces) => {
      if (res.listPieces.data) {
        setData((oldData) => [...oldData, ...res.listPieces.data!]);
        setCount(res.listPieces.count || 0);
      }
      setGroupedData(res.listPieces.grouped_data || []);
    },
  });

  /**
   * Handles click on any row of the table and navigates to single brand
   * @param id id of the row clicked
   */
  const handleClick = (campaignId: string, influencerId: string) => {
    navigate(
      replaceIds({
        url: ROUTES.SINGLE_CAMPAIGN_CONTENT,
        params: [{ campaignId }, { influencerId }],
      }),
    );
  };

  const resetPagination = () => {
    setData([]);
    setGroupedData([]);
    setPageIndex(0);
  };

  const loadMore = () => {
    if (!loadingQuery) {
      refetch({
        ...filters,
        offset: (pageIndex + 1) * CONTROL_INFINITE_SCROLLING_PAGE_SIZE,
        limit: CONTROL_INFINITE_SCROLLING_PAGE_SIZE,
      });
      setPageIndex(pageIndex + 1);
    }
  };

  const updateGroupedData = (id: string, newPieces: SinglePiece[]) => {
    setGroupedData((oldGroupedData) => {
      const newGroupedData = oldGroupedData.map((d) => {
        if (d.id === id)
          return {
            ...d,
            pieces: [...d.pieces, ...newPieces],
          };
        return d;
      });
      return newGroupedData;
    });
  };

  const updateFilters = (newFilters: ControlPiecesFiltersType) => {
    resetPagination();
    setFilters(newFilters);
  };

  return (
    <div className="w-full flex flex-col gap-y-5">
      <Breadcrumbs
        icon={<ControlIcon />}
        breadcrumbs={[
          {
            text: t('controlView.title'),
          },
        ]}
      />
      <div className="flex flex-col gap-y-4">
        <div className="flex flex-row gap-x-2">
          <button
            className={`font-semibold flex flex-none text-sm rounded-[5px] py-2 px-5 ${filters?.has_publication_date ? 'bg-influentia-light-blue text-dark-blue' : 'bg-dark-blue text-white hover:bg-influentia-light-blue hover:text-dark-blue'}`}
            onClick={() => {
              if (!filters?.has_publication_date) {
                updateFilters({
                  ...filters,
                  has_publication_date: true,
                });
              }
            }}
          >
            {t('controlView.statusActions')}
          </button>
          <button
            className={`font-semibold flex flex-none text-sm rounded-[5px] py-2 px-5 ${!filters?.has_publication_date ? 'bg-influentia-light-blue text-dark-blue' : 'bg-dark-blue text-white hover:bg-influentia-light-blue hover:text-dark-blue'}`}
            onClick={() => {
              if (filters?.has_publication_date) {
                updateFilters({
                  ...filters,
                  has_publication_date: false,
                });
              }
            }}
          >
            {t('controlView.noPublicationDate')}
          </button>
        </div>
        <div className="flex flex-row justify-between">
          <div>
            <TableSearchInput
              placeholder={t('controlView.searchInputPlaceholder')}
              filters={{ ...filters }}
              setFilters={updateFilters}
              searchKey="campaign_name"
            />
          </div>
          <div className="flex flex-row gap-x-2">
            <Button
              style={
                filters?.account_manager_campaigns ? 'dark-blue' : 'light-blue'
              }
              text={t('campaigns.myCampaigns')}
              iconLeft={
                filters?.account_manager_campaigns ? (
                  <EyeOffIcon />
                ) : (
                  <EyeIcon />
                )
              }
              customClassnames="rounded-full px-5 border-2 border-influentia-light-blue"
              onClick={() =>
                updateFilters({
                  ...filters,
                  account_manager_campaigns:
                    !filters?.account_manager_campaigns,
                })
              }
            />
            <ButtonDropdown
              icon={<ControlGroupIcon />}
              options={CONTROL_GROUP_DROPDOWN_OPTIONS.map((o) => ({
                ...o,
                label: t(o.label),
              })).filter(
                (o) => o.value !== 'status' || filters?.has_publication_date,
              )}
              value={filters?.grouped || ''}
              onChange={(val) =>
                updateFilters({
                  ...filters,
                  grouped: val.value,
                })
              }
              className="!bg-influentia-light-blue hover:!bg-influentia-black hover:!text-white"
            />
            <ControlPiecesFilters
              setFilters={updateFilters}
              filters={filters || {}}
            />
            <MonthDateFromTo
              onChangeFunction={(month_from, month_to) => {
                updateFilters({
                  ...filters,
                  month_from,
                  month_to,
                });
              }}
              valueFrom={filters?.month_from || ''}
              valueTo={filters?.month_to || ''}
            />
          </div>
        </div>
        <ActiveFilters
          filters={filters || {}}
          setFilters={updateFilters}
          type="action"
        />
        {filters?.grouped ? (
          <>
            {groupedData?.map((g) => (
              <GroupedListelement
                data={g}
                handleClick={handleClick}
                hasMore={false}
                grouped_id={g.id}
                loadMore={() => {}}
                name={g.name}
                type={filters.grouped as any}
                updateGroupedData={(newData) =>
                  updateGroupedData(g.id, newData)
                }
                filters={filters}
              />
            ))}
            {(!groupedData || groupedData.length === 0) && !loadingQuery && (
              <span className="text-center">{t('global.no-data')}</span>
            )}
            {loadingQuery && (
              <div className="w-full mt-5 flex items-center justify-center">
                <Loader />
              </div>
            )}
          </>
        ) : (
          <InfinitePieceList
            data={data}
            handleClick={handleClick}
            hasMore={data.length < count}
            loadMore={loadMore}
            hasPublicationDate={filters?.has_publication_date}
          />
        )}
      </div>
    </div>
  );
}

export default ControlPieces;
